import { createRoot, type Root } from 'react-dom/client'
import { api, Unauthorized, type User } from './client/api.ts'
import { ClientStore } from './client/store.ts'
import { SyncClient } from './client/sync-client.ts'
import { App } from './ui/App.tsx'
import { StoreContext } from './ui/hooks.ts'
import { Login } from './ui/Login.tsx'
import { navigate } from './ui/router.ts'
import '@fontsource-variable/inter'
import './ui/styles.css'

const root: Root = createRoot(document.getElementById('root')!)

async function start(user: User): Promise<void> {
  let store: ClientStore | null = null
  const sync = new SyncClient(user.id, {
    onUnauthorized: () => location.reload(),
    onReinverse: (txnId, inverse) => store?.history.updateInverse(txnId, inverse),
  })
  await sync.start()
  store = new ClientStore(sync)
  store.nav = {
    openNode: (id) => navigate(`/n/${id}`),
    openJournal: (date) => navigate(date ? `/d/${date}` : '/'),
  }
  const logout = async (): Promise<void> => {
    localStorage.removeItem(USER_KEY)
    await api.logout().catch(() => {})
    await sync.logoutAndForget()
    location.assign('/')
  }
  ;(window as unknown as { __solar: unknown }).__solar = { store, sync }
  root.render(
    <StoreContext.Provider value={store}>
      <App user={user} onLogout={() => void logout()} />
    </StoreContext.Provider>,
  )
  store.warmInBackground()
}

const USER_KEY = 'solar-user'

/**
 * Start: ask the server who is signed in; without a network, start from the mirror as the last
 * user (offline reading, SPEC §12). Only a real "not signed in" shows the login.
 */
async function boot(): Promise<void> {
  try {
    const { user } = await api.me()
    localStorage.setItem(USER_KEY, JSON.stringify(user))
    await start(user)
  } catch (e) {
    if (e instanceof Unauthorized) {
      localStorage.removeItem(USER_KEY)
      root.render(
        <Login
          onDone={(user) => {
            localStorage.setItem(USER_KEY, JSON.stringify(user))
            void start(user)
          }}
        />,
      )
      return
    }
    const last = localStorage.getItem(USER_KEY)
    if (last) await start(JSON.parse(last) as User)
    else root.render(<div className="notice">Could not reach Solar: {e instanceof Error ? e.message : String(e)}</div>)
  }
}

if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  addEventListener('load', () => void navigator.serviceWorker.register('/sw.js').catch((e) => console.warn('solar: no service worker', e)))
}

void boot()
